import { EventEmitter } from '@angular/core';

import * as SVG from 'svg.js';

import { TransformationConstraints, TransformHandle
       } from 'app/controls/transform-handle/transform-handle';
import { GalleryService } from 'app/gallery/gallery.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { StageConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { LoggingService } from 'core/logging/logging.service';
import { StageUtils } from 'core/utils.stage';
import { ActionBarComponent } from './overlays/action-bar/action-bar.component';
import { ActionBarDirective } from './overlays/action-bar/action-bar.directive';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';

export class TransformationError extends TrivialError
{
  // i18n.error.transformationError.title
  // i18n.error.transformationError.message
  protected _name: string = 'TransformationError';
}

export enum PlaceholderType
{
  Image,
  Text,
}

export enum ChangeIntent {
  InsertText,
  ChangeFontSize,
  ChangeFontFamily,
  ChangeAlignment,
  ChangeColor,
  Rollback,
  Copy,
  CheckCharacterAvailablity,
  Unspecified,
}

export enum FocusEvent
{
  FocusEmpty,
  FocusFilled,
  Blur,
}

class Transaction
{
  public active: boolean = true;

  constructor(
    private rollbackAction: () => void, private cleanAction: () => void)
  {}

  public commit(): void {
    this.active = false;
    this.cleanAction();
  }

  public rollback(): void {
    this.rollbackAction();
    this.active = false;
  }
}

export abstract class Placeholder
{
  private transactions: Array<Transaction> = [];
  private _hasFocus: boolean = false;
  private _hasForceFocus: boolean = false;
  private readonly wrapperGroupEventListeners: Object = {
    'click': this.wrapperClicked.bind(this),
  };
  private readonly baseEventListeners: Object = {
    'tab': this.showEditMarker.bind(this)
  };
  private transformHandle: TransformHandle | undefined = undefined;

  public wrapper: SVG.Doc;
  public group: SVG.G;

  public clicked: EventEmitter<void> = new EventEmitter<void>();
  public finished: EventEmitter<any> = new EventEmitter<FocusEvent>();
  public changed: EventEmitter<string> = new EventEmitter<string>();
  public focused: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  public destroyed: EventEmitter<boolean> = new EventEmitter<boolean>();

  protected editMarker: SVG.G | undefined = undefined;
  private highlightMarker: SVG.G | undefined = undefined;
  private hoverMarker: SVG.G | undefined = undefined;
  private actionBar: ActionBarComponent | undefined = undefined;

  public abstract hasEditableContent(): boolean;
  public abstract isFlexible(): boolean;
  public abstract extractInformation(): Promise<any>;

  constructor(
      protected errorService: ErrorService,
      protected loggingService: LoggingService,
      protected designStepService: DesignStepService,
      protected galleryService: GalleryService,
      private toolboxService: ToolboxService,
      protected placeholderContentService: PlaceholderContentService,
      protected placeholderService: PlaceholderService,
      private actionBarContainer: ActionBarDirective,
      public element: any,
      protected partId: string,
      public type: PlaceholderType,
      private isLocked: boolean,
  )
  {
    if (element.parents().length === 0) {
      return undefined;
    }

    this.wrapper = element.parents()[0] as SVG.Doc;
    this.wrapper.attr('text-rendering', 'geometricPrecision');

    if (this.wrapper.parents()[0] === undefined) {
      return undefined;
    }

    const wrapperOfParentIsGroup = this.wrapper.parents()[0] &&
        this.wrapper.parents()[0].node.tagName === 'g';
    this.group = wrapperOfParentIsGroup
        ? this.wrapper.parents()[0] as SVG.G
        : undefined;

    if (this.isContentChangeable()) {
      for (const listener in this.baseEventListeners) {
        if (this.baseEventListeners.hasOwnProperty(listener)) {
          this.wrapper.node.addEventListener(
              listener, this.baseEventListeners[listener]);
        }
      }
      const wrapperGroup = this.wrapper.node.parentNode;
      for (const listener in this.wrapperGroupEventListeners) {
        if (this.wrapperGroupEventListeners.hasOwnProperty(listener)) {
          wrapperGroup.addEventListener(
              listener, this.wrapperGroupEventListeners[listener]);
        }
      }
    }

    if (this.isLinked() && !this.isContentChangeable()) {
      const linkedNode = this.group ? this.group.node : this.wrapper.node;
      linkedNode.style['pointer-events'] = 'none';
    }
  }

  public async destructor(): Promise<void>
  {
    if (this.isContentChangeable()) {
      for (const listener in this.baseEventListeners) {
        if (this.baseEventListeners.hasOwnProperty(listener)) {
          this.wrapper.node.removeEventListener(
              listener, this.baseEventListeners[listener]);
        }
      }
      const wrapperGroup = this.wrapper.node.parentNode;
      for (const listener in this.wrapperGroupEventListeners) {
        if (this.wrapperGroupEventListeners.hasOwnProperty(listener)) {
          wrapperGroup.removeEventListener(
              listener, this.wrapperGroupEventListeners[listener]);
        }
      }

      this.blur(true);
    }
  }

  public async remove(): Promise<void>
  {
    this.destroyed.emit(true);
    await this.destructor();
    this.wrapper.node.remove();
  }

  public async reset(): Promise<void>
  {
    this.detachContent();
    this.blur(true);
  }

  public highlight(): void
  {}

  public finalize(): void
  {
    this.blur();

    for (const transaction of this.transactions.filter(el => el.active)) {
      transaction.rollback();
    }
  }

  public startTransaction(rollback: () => void): Transaction
  {
    const t = new Transaction(rollback, this.cleanTransactions.bind(this));
    this.transactions.push(t);
    return t;
  }

  private cleanTransactions(): void
  {
    this.transactions = this.transactions.filter(el => el.active);
  }

  public isUnchanged(): boolean
  {
    return StageUtils.getBooleanAttribute(
        this.element.node, StageConstants.ATTRIBUTE_IS_UNCHANGED);
  }

  public isContentChangeable(): boolean
  {
    if (this.isLocked) {
      return false;
    }

    return StageUtils.isContentChangeable(this.wrapper.node);
  }

  public isLinked(): boolean
  {
    const el = this.element.node;
    return el.hasAttribute(StageConstants.ATTRIBUTE_COPY_FROM) &&
        el.getAttribute(StageConstants.ATTRIBUTE_COPY_FROM) !== 'false';
  }

  public isPending(): boolean
  {
    return this.element.node.hasAttribute(StageConstants.ATTRIBUTE_PENDING);
  }

  public get zoomMode(): string
  {
    return this.wrapper.attr(StageConstants.ATTRIBUTE_ZOOM_MODE).toLowerCase();
  }

  public set(attributeName: string, attributeValue?: any): void
  {
    if (attributeValue === undefined) {
      this.removeAttribute(attributeName);
    }
    else {
      this.element.attr(attributeName, attributeValue);
    }
  }

  public get(attributeName: string): string
  {
    return this.element.attr(attributeName);
  }

  private removeAttribute(attributeName: string): void
  {
    this.element.node.removeAttribute(attributeName);
  }

  public hideEditMarker(): void
  {
    if (this.editMarker) {
      this.editMarker.remove();
      this.editMarker = undefined;
    }
  }

  private get dragImageOverTextPlaceholder(): boolean
  {
    return this.type === PlaceholderType.Text &&
        this.galleryService.getDraggingImage() !== undefined;
  }

  protected showEditMarker(): void
  {
    if (!this.editMarker && !this.dragImageOverTextPlaceholder) {
      this.editMarker = this
        .drawMarker()
        .addClass(StageConstants.CLASS_NAME_EDIT_MARKER);
    }
  }

  private showHoverMarker(): void
  {
    if (!this.hoverMarker && !this.dragImageOverTextPlaceholder) {
      this.hoverMarker = this
        .drawMarker()
        .addClass(StageConstants.CLASS_NAME_HOVER_MARKER);
    }
  }

  private hideHoverMarker(): void
  {
    if (this.hoverMarker) {
      this.hoverMarker.remove();
      this.hoverMarker = undefined;
   }
  }

  protected shouldInjectActionBar(): boolean
  {
    return this.hasEditableContent();
  }

  private async injectActionBar(
      startActionIfPossible: boolean = false): Promise<void>
  {
    if (this.isFlexible()) {
      await this.setTransformHandle();
    }

    if (this.shouldInjectActionBar()) {
      if (!this.actionBar) {
        const positioningHints = this.transformHandle
          ? this.transformHandle.actionBarPositioningHints
          : undefined;

        this.actionBar = this.actionBarContainer.createComponent();
        this.actionBar.initialize(this, positioningHints);
      }
      if (startActionIfPossible) {
        await this.actionBar.startActionIfPossible();
      }
    }
  }

  private removeActionBar(): void
  {
    if (this.actionBar) {
      this.actionBar.destroy();
      this.actionBar = undefined;
    }
  }

  protected showHighlightMarker(): void
  {
    this.hideHighlightMarker();

    this.highlightMarker = this.drawMarker().addClass(
        StageConstants.CLASS_NAME_HIGHLIGHT_MARKER);

    setTimeout(
        this.hideHighlightMarker.bind(this), StageConstants.HIGHLIGHT_TIMEOUT);
  }

  protected hideHighlightMarker(): void
  {
    if (this.highlightMarker) {
      this.highlightMarker.remove();
      this.highlightMarker = undefined;
    }
  }

  private drawMarker(): SVG.G
  {
    const container = this.wrapper.parent() as any;
    const root = container.clone().clear();

    root.attr('pointer-events', 'none');
    root.node.removeAttribute('style');

    root.rect()
      .move(this.wrapper.x(), this.wrapper.y())
      .size(this.wrapper.width(), this.wrapper.height());
    return root;
  }

  public async focus(force: boolean = false): Promise<void>
  {
    if (force) {
      this._hasForceFocus = true;
      this.showEditMarker();
    }
    else if (!this._hasForceFocus) {
      this.showHoverMarker();
    }

    this._hasFocus = true;
    this.hideHighlightMarker();

    if (StageConstants.SHOW_ACTION_BAR_ON_HOVER) {
      await this.injectActionBar(force);
    }

    this.focused.emit(
      this.hasEditableContent()
        ? FocusEvent.FocusFilled
        : FocusEvent.FocusEmpty);
  }

  protected async mouseOver(): Promise<void>
  {
    if (!this.placeholderService.hasTransformHandle()) {
      await this.focus();
    }
  }

  public mouseLeave(event: MouseEvent): boolean
  {
    event.stopPropagation();
    if ((this.actionBar &&
        this.actionBar.nativeElement.contains(event.relatedTarget as Node)) ||
        this.wrapper.node.contains(event.relatedTarget as Node)) {
      return false;
    }

    this.blur();
    this.wrapper.node.style.cursor = 'default';
  }

  public blur(force: boolean = false): void
  {
    if (!force && this._hasForceFocus) {
      return;
    }

    this.hideEditMarker();
    this.hideHighlightMarker();
    this.hideHoverMarker();
    this.removeActionBar();
    this.removeTransformHandle();

    if (! this.hasFocus()) {
      return;
    }
    this._hasFocus = false;
    this._hasForceFocus = false;
    this.focused.emit(FocusEvent.Blur);
  }

  public hasFocus(): boolean
  {
    return this._hasFocus;
  }

  protected async clickEvent(event?: MouseEvent): Promise<void>
  {
    if (!this.isContentChangeable()) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    if (this._hasForceFocus) {
      return this.doubleClick();
    }

    const placeholderHadTransformHandle =
      this.placeholderService.hasTransformHandle();

    this.placeholderService.blurAllPlaceholders(true);

    if (placeholderHadTransformHandle) {
      return;
    } else {
      await this.focus(true);
      if (StageConstants.SHOW_ACTION_BAR_ON_CLICK) {
        await this.injectActionBar(true);
      }

      this.clicked.emit();
    }
  }

  public getPartId(): string
  {
    return this.partId;
  }

  public async commitContent(): Promise<void>
  {
    this.set(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'false');
    await this.placeholderContentService.savePlaceholderContent(this);
  }

  public detachContent(): void
  {
    this.set(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'true');
  }

  public async restoreContent(): Promise<void>
  {
    const info =
        await this.placeholderContentService.restorePlaceholderContent(this);

    if (info) {
      this.set(
        StageConstants.ATTRIBUTE_IS_UNCHANGED,
        info.isUnchanged ? 'true' : 'false');
    }
  }

  public async saveContentIfChanged(): Promise<void>
  {
    if (!this.isUnchanged()) {
      await this.placeholderContentService.savePlaceholderContent(this);
    }
  }

  protected async getMinSize(): Promise<TransformationConstraints>
  {
    return {
      minWidth: 1,
      minHeight: 1
    };
  }

  private whileTransformation(): void
  {
    if (this.actionBar) {
      this.actionBar.setTargetRegion();
    }
    if (this.editMarker) {
      const container = <any>this.wrapper.parent();
      this.editMarker.last()
        .move(this.wrapper.x(), this.wrapper.y())
        .size(this.wrapper.width(), this.wrapper.height());
      this.editMarker.attr('transform', container.attr('transform'));
    }
  }

  protected async afterTransformationDone(): Promise<void>
  {
    if (this.actionBar) {
      this.actionBar.updateFittingSide();
    }
  }

  public async setTransformHandle(): Promise<void>
  {
    if (!this.hasTransformHandle()) {
      this.transformHandle = new TransformHandle(
          this.wrapper,
          await this.getMinSize(),
          this.whileTransformation.bind(this),
          await this.afterTransformationDone.bind(this),
          await this.clickEvent.bind(this));

      StageUtils.raiseLayersToFront(
        this.element.doc(), [StageConstants.GROUP_TAG_DESIGN_ONLY]);
    }
  }

  public hasTransformHandle(): boolean
  {
    return !!this.transformHandle;
  }

  public removeTransformHandle(): void
  {
    if (this.hasTransformHandle()) {
      this.transformHandle.teardown();
      this.transformHandle = undefined;
      StageUtils.ensureCorrectLayerPositions(this.element.doc());
    }
  }

  private async wrapperClicked(event: MouseEvent): Promise<void>
  {
    if (! this.hasTransformHandle()) {
      await this.clickEvent(event);
    }
  }

  public doubleClick(event?: Event): void
  {
    if (event) {
      event.stopPropagation();
    }

    this.removeTransformHandle();
    this.toolboxService.edit(this);
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/placeholder/placeholder.ts